// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
    // XNG-Change
    showERPNumber?: boolean;
    isTableView?: boolean;
}

type Props = OwnProps;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
    // XNG-Change
    erpNumberLink?: LinkPresentationProps;
    productDetailSubDescription?: TypographyProps;
    erpDescriptionWrapper?: InjectableCss;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescription = ({ product, onClick, extendedStyles, showERPNumber = true, isTableView }: Props) => {
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));

    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    // XNG-Change
    const erpNumber = "product" in product ? product.product.productNumber : (product as CartLineModel).erpNumber || (product as OrderLineModel).productErpNumber;
    const showLink = productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct);
    if (showERPNumber) {
        return (
            <StyledWrapper {...styles.erpDescriptionWrapper}>
                {showLink && productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
                    <Link
                        href={productDetailPath}
                        {...styles.erpNumberLink}
                        alt={`See product details for ${description}`}
                    >
                        {erpNumber}
                    </Link>
                ) : (
                    <Typography {...styles.erpNumberLink?.typographyProps}>{erpNumber}</Typography>
                )}
                <Typography {...styles.productDetailSubDescription} className={isTableView ? "title-table-view" : ""}>{description}</Typography>
            </StyledWrapper>
        );
    }
    // </XNG-Change>
    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            onClick={onClick}
            data-test-selector="productDescriptionLink"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default ProductDescription;
